import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  DescriptionOutlined as DescriptionOutlinedIcon,
  OpenInNew as OpenInNewIcon,
  PictureAsPdfOutlined as PictureAsPdfOutlinedIcon,
  ReceiptLong as ReceiptLongIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { hostApiClient } from '../../hostApiClient';
import {
  SubscriptionInvoice,
  SubscriptionInvoiceStatus,
  formatInvoiceAmount,
} from '../../models/Subscription';
import LedgerPagination from '../common/LedgerPagination';
import {
  DEFAULT_LEDGER_ROWS_PER_PAGE,
} from '../common/LedgerRowsPerPage';

function invoiceStatusColor(
  status: SubscriptionInvoiceStatus,
): 'success' | 'info' | 'warning' | 'error' | 'default' {
  switch (status) {
    case SubscriptionInvoiceStatus.PAID:
      return 'success';
    case SubscriptionInvoiceStatus.OPEN:
      return 'info';
    case SubscriptionInvoiceStatus.DRAFT:
      return 'default';
    case SubscriptionInvoiceStatus.VOID:
    case SubscriptionInvoiceStatus.UNCOLLECTIBLE:
      return 'error';
    default:
      return 'default';
  }
}

function displayAmount(invoice: SubscriptionInvoice): number {
  if (invoice.status === SubscriptionInvoiceStatus.PAID && invoice.amountPaid > 0) {
    return invoice.amountPaid;
  }
  return invoice.amountDue;
}

/**
 * Stripe invoice ledger for the host billing page.
 * Aesthetic: calm operations ledger — one job (history), status chips, external view/PDF only.
 */
const SubscriptionInvoiceHistory: React.FC = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();

  const [items, setItems] = useState<SubscriptionInvoice[]>([]);
  const [hasMore, setHasMore] = useState(false);
  /** Cursor (invoice id) for each page index; index 0 is always undefined. */
  const [cursorStack, setCursorStack] = useState<(string | undefined)[]>([undefined]);
  const [pageIndex, setPageIndex] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_LEDGER_ROWS_PER_PAGE);
  const [initialLoading, setInitialLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isFirstLoadRef = React.useRef(true);

  const loadAtCursor = useCallback(
    async (
      startingAfter: string | undefined,
      options?: {
        nextPageIndex?: number;
        nextCursorStack?: (string | undefined)[];
        clearOnError?: boolean;
        replaceList?: boolean;
      },
    ) => {
      const replaceList = Boolean(options?.replaceList);
      try {
        if (replaceList) {
          setInitialLoading(true);
        } else {
          setPageLoading(true);
        }
        setError(null);
        const page = await hostApiClient.getSubscriptionInvoices({
          limit: rowsPerPage,
          startingAfter,
        });
        setItems(page.items);
        setHasMore(page.hasMore);
        if (options?.nextPageIndex != null) {
          setPageIndex(options.nextPageIndex);
        }
        if (options?.nextCursorStack) {
          setCursorStack(options.nextCursorStack);
        }
      } catch (err) {
        console.error('Error loading subscription invoices:', err);
        setError(t('subscription.invoices.fetchError', 'Failed to load invoices'));
        if (options?.clearOnError) {
          setItems([]);
          setHasMore(false);
          setPageIndex(0);
          setCursorStack([undefined]);
        }
      } finally {
        setInitialLoading(false);
        setPageLoading(false);
      }
    },
    [rowsPerPage, t],
  );

  useEffect(() => {
    const replaceList = isFirstLoadRef.current;
    isFirstLoadRef.current = false;
    void loadAtCursor(undefined, {
      nextPageIndex: 0,
      nextCursorStack: [undefined],
      clearOnError: true,
      replaceList,
    });
  }, [loadAtCursor]);

  const handleRowsPerPageChange = (next: number) => {
    setRowsPerPage(next);
  };

  const busy = initialLoading || pageLoading;

  const handlePrevious = () => {
    if (pageIndex <= 0 || busy) return;
    const prevIndex = pageIndex - 1;
    void loadAtCursor(cursorStack[prevIndex], { nextPageIndex: prevIndex });
  };

  const handleNext = () => {
    if (!hasMore || busy || items.length === 0) return;
    const last = items[items.length - 1];
    if (!last) return;
    const nextIndex = pageIndex + 1;
    const nextStack = cursorStack.slice(0, pageIndex + 1);
    nextStack[nextIndex] = last.id;
    void loadAtCursor(last.id, {
      nextPageIndex: nextIndex,
      nextCursorStack: nextStack,
    });
  };

  const handleRetry = () => {
    void loadAtCursor(undefined, {
      nextPageIndex: 0,
      nextCursorStack: [undefined],
      clearOnError: true,
      replaceList: true,
    });
  };

  const formatDate = (value: string | null | undefined) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const openExternal = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Box
      sx={{
        borderRadius: 2,
        border: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        overflow: 'hidden',
      }}
    >
      <Stack
        direction="row"
        spacing={1.25}
        alignItems="center"
        sx={{
          px: { xs: 2, sm: 2.5 },
          py: 1.75,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: alpha(theme.palette.primary.main, 0.04),
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1,
            display: 'grid',
            placeItems: 'center',
            bgcolor: alpha(theme.palette.primary.main, 0.12),
          }}
        >
          <ReceiptLongIcon sx={{ fontSize: 18, color: 'primary.main' }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="subtitle1"
            component="h2"
            fontWeight={800}
            sx={{ letterSpacing: '-0.01em' }}
          >
            {t('subscription.invoices.title', 'Invoice history')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t(
              'subscription.invoices.subtitle',
              'Past Stripe invoices for this organization. Open or download from Stripe.',
            )}
          </Typography>
        </Box>
      </Stack>

      {initialLoading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={140} px={2}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!initialLoading && error && (
        <Box sx={{ p: 2 }}>
          <Alert
            severity="error"
            sx={{ borderRadius: 2 }}
            action={
              <Button color="inherit" size="small" onClick={handleRetry}>
                {t('subscription.invoices.retry', 'Retry')}
              </Button>
            }
          >
            {error}
          </Alert>
        </Box>
      )}

      {!initialLoading && !error && items.length === 0 && (
        <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 3 }}>
          <Typography variant="body2" color="text.secondary">
            {t(
              'subscription.invoices.empty',
              'No invoices yet. Charges appear here after your trial or first billing cycle.',
            )}
          </Typography>
        </Box>
      )}

      {!initialLoading && !error && items.length > 0 && (
        <Stack
          divider={<Divider flexItem />}
          sx={{
            pb: 0,
            opacity: pageLoading ? 0.55 : 1,
            transition: 'opacity 120ms ease',
            pointerEvents: pageLoading ? 'none' : 'auto',
          }}
          aria-busy={pageLoading}
        >
          {items.map((invoice) => {
            const createdLabel = formatDate(invoice.createdAt);
            const title =
              invoice.description?.trim() ||
              invoice.number ||
              t('subscription.invoices.untitled', 'Invoice');

            return (
              <Stack
                key={invoice.id}
                direction="row"
                spacing={{ xs: 1.25, sm: 1.75 }}
                alignItems="center"
                sx={{ px: { xs: 2, sm: 2.5 }, py: 1.75 }}
              >
                <Chip
                  label={t(`subscription.invoices.status.${invoice.status}`, invoice.status)}
                  color={invoiceStatusColor(invoice.status)}
                  sx={{
                    fontWeight: 800,
                    fontSize: '0.8125rem',
                    height: 32,
                    px: 0.5,
                    flexShrink: 0,
                    '& .MuiChip-label': { px: 1.25 },
                  }}
                />

                <Stack spacing={0.25} sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body1" fontWeight={700} sx={{ minWidth: 0 }}>
                    {title}
                  </Typography>
                  {createdLabel && (
                    <Typography variant="body2" color="text.secondary">
                      {createdLabel}
                    </Typography>
                  )}
                </Stack>

                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ flexShrink: 0 }}
                >
                  <Typography variant="body1" fontWeight={800} sx={{ letterSpacing: '-0.01em' }}>
                    {formatInvoiceAmount(displayAmount(invoice), invoice.currencyCode, i18n.language)}
                  </Typography>
                  <Stack direction="row" spacing={0.25}>
                    {invoice.hostedInvoiceUrl && (
                      <Tooltip title={t('subscription.invoices.view', 'View invoice')}>
                        <IconButton
                          size="small"
                          aria-label={t('subscription.invoices.view', 'View invoice')}
                          onClick={() => openExternal(invoice.hostedInvoiceUrl!)}
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {invoice.invoicePdfUrl && (
                      <Tooltip title={t('subscription.invoices.downloadPdf', 'Download PDF')}>
                        <IconButton
                          size="small"
                          aria-label={t('subscription.invoices.downloadPdf', 'Download PDF')}
                          onClick={() => openExternal(invoice.invoicePdfUrl!)}
                        >
                          <PictureAsPdfOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {!invoice.hostedInvoiceUrl && !invoice.invoicePdfUrl && (
                      <Tooltip title={t('subscription.invoices.noLinks', 'No Stripe links yet')}>
                        <span>
                          <IconButton size="small" disabled aria-label={t('subscription.invoices.noLinks', 'No Stripe links yet')}>
                            <DescriptionOutlinedIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </Stack>
                </Stack>
              </Stack>
            );
          })}
        </Stack>
      )}

      {!error && items.length > 0 && (
        <Box
          sx={{
            px: { xs: 2, sm: 2.5 },
            py: 1.5,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          <LedgerPagination
            id="subscription-invoices"
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            canGoPrevious={pageIndex > 0}
            canGoNext={hasMore}
            onPrevious={handlePrevious}
            onNext={handleNext}
            disabled={busy}
            label={t('common.page', 'Page') + ` ${pageIndex + 1}`}
          />
        </Box>
      )}
    </Box>
  );
};

export default SubscriptionInvoiceHistory;
